import React from 'react';

import dayjs from 'dayjs';

import ContentCard from '../../../components/ContentCard';
import DataGrid from '../../../components/DataGrid';
import { Class } from '../../../services/classes/types';
import { Course } from '../../../services/courses/types';
import { UserGroup } from '../../../types/common';
import compareDayLabels from '../../../utils/common/compareDayLabels';
import useUserGroups from '../../../utils/react/hooks/useUserGroups';

type ExtendedClass = Class & {
  dayLabel: string;
  timeLabel: string;
  dayTimeLabel: string;
};

interface ClassesCardProps {
  courses: Course[];
  allClasses: ExtendedClass[];
  validClasses: ExtendedClass[];
}

const ClassesCard = ({ courses, allClasses, validClasses }: ClassesCardProps) => {
  const userGroups = useUserGroups();
  const isAdmin = userGroups.includes(UserGroup.Admins);

  return (
    <ContentCard variant="outlined" title={`Classes (${validClasses.length} / ${allClasses.length} Valid)`}>
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: 'dayTimeLabel', sort: 'asc' }],
          },
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        pageSizeOptions={[5]}
        rows={allClasses}
        columns={[
          {
            field: 'course',
            headerName: 'Course',
            width: 160,
            renderCell: (e: any) => courses.find((c) => c.id === e.value)?.label || 'Unknown',
          },
          {
            field: 'dayTimeLabel',
            headerName: 'Day / Time',
            width: 150,
            // A custom sortComparator is needed as the lexical order of days
            // is not the order in which they appear in the week
            sortComparator: (v1, v2, param1, param2) => {
              const [day1, time1] = param1.value.split(' ');
              const [day2, time2] = param2.value.split(' ');

              const dayCompare = compareDayLabels(day1, day2);

              if (dayCompare === 0) {
                // If day is the same, compare by time
                return dayjs(time1, 'h:mm a').valueOf() - dayjs(time2, 'h:mm a').valueOf();
              }

              return dayCompare;
            },
          },
          {
            field: 'studentCount',
            headerName: '#',
            width: 40,
          },
          {
            field: 'nextLessonDatetime',
            headerName: 'Next Lesson',
            width: 120,
            renderCell: ({ row }) => (row.nextLessonDatetime ? dayjs(row.nextLessonDatetime).format('DD MMM YYYY') : 'None'),
          },
        ]}
        getRowHref={(row) => `/${isAdmin ? 'admin' : 'teacher'}/class/${row.subject}/${row.classId}`}
      />
    </ContentCard>
  );
};

export default ClassesCard;
